/**
 * Electronic-signature widget for the client contract review flow.
 *
 * Renders the personalised PDF inline so the client can read it without
 * leaving the page, then collects legal name + consent and POSTs to
 * POST /contracts/cases/{id}/sign. All stamping is server-side — no file
 * upload from the browser is required.
 *
 * The screen states only what the portal records and claims no legal effect (#283): what a
 * signature means in law is for counsel's text, not for this widget.
 */
import { useEffect, useState } from 'react';
import contractsService from '../../services/contracts.service';
import type { UsOfferingInfo } from '../../types/contracts';
import UsOfferingSection, {
  initialUsOfferingState,
  usOfferingAnswers,
  usOfferingReady,
  type UsOfferingState,
} from './UsOfferingSection';
import { Link } from 'react-router-dom';
import { getApiErrorMessage } from '../../lib/apiError';
import { isTaxFormRequired, taxFormPath } from '../../lib/taxForm';
import { prefersReducedMotion, successHoldMs } from '../../lib/successFlash';
import { colors } from '../../styles/theme';

const NOT_ALLOWED = 'not-allowed';

interface Props {
  readonly caseId: string;
  readonly isReUpload: boolean;
  /** Pre-filled from the authenticated user's profile; editable by the user. */
  readonly defaultSignerName: string;
  /** Called after the server confirms the signing and FSM transitions. */
  readonly onSignComplete: () => void;
}

type Phase = 'loading-pdf' | 'ready' | 'signing' | 'signed' | 'done' | 'pdf-error';

/** The refusal of a signature, tied to the controls it is about (#362). */
const SIGN_ERROR_ID = 'sign-error';

function signerNameDescription(signError: string | null): string {
  return signError === null ? 'signer-name-hint' : `signer-name-hint ${SIGN_ERROR_ID}`;
}

function errorDescription(signError: string | null): string | undefined {
  return signError === null ? undefined : SIGN_ERROR_ID;
}

function PdfPreview({
  pdfUrl,
  isReUpload,
}: {
  readonly pdfUrl: string | null;
  readonly isReUpload: boolean;
}): JSX.Element {
  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
      }}>
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          color: colors.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          {isReUpload ? 'Revised document to sign' : 'Document to sign'}
        </span>
        {pdfUrl !== null && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 12,
              color: colors.navy,
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Open in new tab ↗
          </a>
        )}
      </div>
      {pdfUrl === null ? null : (
        <iframe
          src={pdfUrl}
          title="Contract document"
          style={{
            width: '100%',
            height: 500,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            background: colors.canvas,
            display: 'block',
          }}
        />
      )}
      <p id="contract-read-hint" className="text-xs mt-2" style={{ color: colors.mutedSoft }}>
        Scroll through the entire document before signing. Use &quot;Open in new tab&quot; to
        download or print a copy for your records.
      </p>
    </div>
  );
}

export default function ContractSigningWidget({
  caseId,
  isReUpload,
  defaultSignerName,
  onSignComplete,
}: Props): JSX.Element {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('loading-pdf');
  const [signerName, setSignerName] = useState(defaultSignerName);
  const [consented, setConsented] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [taxFormNeeded, setTaxFormNeeded] = useState(false);
  // A US series asks something more at the signature (null for every other contract).
  const [usInfo, setUsInfo] = useState<UsOfferingInfo | null>(null);
  const [usState, setUsState] = useState<UsOfferingState | null>(null);
  // The steps the server says a Rule 506(b) signature still needs (null until it has said).
  const [stepsOwed, setStepsOwed] = useState<readonly string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    contractsService
      .getUsOffering(caseId)
      .then((info) => {
        if (cancelled || info === null) return;
        setUsInfo(info);
        setUsState(initialUsOfferingState(info));
      })
      .catch(() => {
        // Display only: the signature itself refuses what it needs and says so.
      });
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  useEffect(() => {
    let cancelled = false;
    contractsService
      .getDownloadUrl(caseId)
      .then(({ download_url }) => {
        if (cancelled) return;
        setPdfUrl(download_url);
        setPhase('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPdfError(getApiErrorMessage(err, 'Could not load the document for review.'));
        setPhase('pdf-error');
      });
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  async function handleSign(): Promise<void> {
    const name = signerName.trim();
    if (!name) {
      setSignError('Please enter your full legal name before signing.');
      return;
    }
    if (!consented) {
      setSignError('Please confirm you have read and agree to the document.');
      return;
    }
    if (usInfo !== null && usState !== null && !usOfferingReady(usInfo, usState, stepsOwed)) {
      setSignError('Please complete the questions above before signing.');
      return;
    }
    setSignError(null);
    setPhase('signing');
    try {
      await contractsService.signContract(
        caseId,
        name,
        usState === null ? undefined : usOfferingAnswers(usInfo, usState),
      );
      // Brief "✓ Signed!" confirmation so the moment of signature feels
      // deliberate before the parent transitions (#53). Instant under
      // reduced motion.
      const hold = successHoldMs(prefersReducedMotion());
      if (hold === 0) {
        setPhase('done');
        onSignComplete();
      } else {
        setPhase('signed');
        globalThis.setTimeout(() => {
          setPhase('done');
          onSignComplete();
        }, hold);
      }
    } catch (err: unknown) {
      setPhase('ready');
      setTaxFormNeeded(isTaxFormRequired(err));
      setSignError(getApiErrorMessage(err, 'Signing failed. Please try again.'));
    }
  }

  if (phase === 'signed') {
    return (
      <output
        style={{
          display: 'block',
          padding: '32px 0',
          textAlign: 'center',
          color: colors.success,
          fontSize: 15,
          fontWeight: 700,
        }}
      >
        ✓ Signed! Submitting for review…
      </output>
    );
  }

  if (phase === 'loading-pdf') {
    return (
      <div style={{ padding: '32px 0', textAlign: 'center', color: colors.muted, fontSize: 14 }}>
        Loading document…
      </div>
    );
  }

  if (phase === 'pdf-error') {
    return (
      <div
        className="rounded-lg p-4 text-sm"
        style={{
          background: colors.dangerBg,
          color: colors.danger,
          border: `1px solid ${colors.dangerBorder}`,
        }}
        role="alert"
      >
        {pdfError ?? 'Could not load the document. Please refresh and try again.'}
      </div>
    );
  }

  const isSigning = phase === 'signing';
  const usReady =
    usInfo === null || usState === null || usOfferingReady(usInfo, usState, stepsOwed);
  const canSign = consented && usReady && signerName.trim().length > 0 && !isSigning;
  // Marked only once a signature was refused for it, never while the person is still typing.
  const nameMissing = signError !== null && signerName.trim().length === 0;
  const submitLabel = isReUpload ? 'Re-sign & Submit' : 'Sign document';
  const signButtonLabel = isSigning ? 'Signing…' : submitLabel;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── PDF preview ─────────────────────────────────────────── */}
      <PdfPreview pdfUrl={pdfUrl} isReUpload={isReUpload} />

      {/* ── Consent + signature block ────────────────────────────── */}
      <div style={{
        padding: '20px 22px',
        borderRadius: 12,
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>

        {/* Legal name */}
        <div>
          <label
            htmlFor="signer-name"
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: colors.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'block',
              marginBottom: 6,
            }}
          >
            Full legal name
          </label>
          <input
            id="signer-name"
            type="text"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            disabled={isSigning}
            maxLength={200}
            required
            aria-describedby={signerNameDescription(signError)}
            aria-invalid={nameMissing}
            placeholder="Enter your full legal name as it appears on your ID"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              fontSize: 15,
              fontWeight: 600,
              border: `2px solid ${colors.controlBorder}`,
              borderRadius: 8,
              outline: 'none',
              color: colors.ink,
              background: isSigning ? colors.canvas : colors.surface,
              cursor: isSigning ? NOT_ALLOWED : undefined,
            }}
          />
          <p id="signer-name-hint" className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
            This name will be printed in the signature block on the document.
          </p>
        </div>

        {/* The questions of a US series: Regulation S certification or Rule 506(b) answer */}
        {usInfo !== null && usState !== null && (
          <UsOfferingSection
            caseId={caseId}
            info={usInfo}
            state={usState}
            disabled={isSigning}
            onChange={setUsState}
            onOwedChange={setStepsOwed}
          />
        )}

        {/* Consent checkbox */}
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: isSigning ? NOT_ALLOWED : 'pointer',
        }}>
          <input
            type="checkbox"
            checked={consented}
            aria-describedby={errorDescription(signError)}
            onChange={(e) => setConsented(e.target.checked)}
            disabled={isSigning}
            style={{
              marginTop: 3,
              flexShrink: 0,
              width: 16,
              height: 16,
              cursor: isSigning ? NOT_ALLOWED : 'pointer',
            }}
          />
          <span style={{ fontSize: 13, color: colors.inkSoft, lineHeight: 1.55 }}>
            I confirm that I have read and understood this document in its entirety
            and agree to its terms and conditions. I understand that clicking
            &ldquo;{isReUpload ? 'Re-sign & Submit' : 'Sign document'}&rdquo;
            signs it electronically in the portal, and that my identity, the time and
            the exact document are recorded.
          </span>
        </label>

        {signError !== null && (
          <div
            id={SIGN_ERROR_ID}
            className="rounded-lg p-3 text-sm"
            style={{
              background: colors.dangerBg,
              color: colors.danger,
              border: `1px solid ${colors.dangerBorder}`,
            }}
            role="alert"
          >
            {signError}
            {taxFormNeeded && (
              <>
                {' '}
                <Link to={taxFormPath()} style={{ color: colors.ink, fontWeight: 600 }}>
                  Review my tax form →
                </Link>
              </>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            type="button"
            onClick={() => handleSign()}
            disabled={!canSign}
            aria-describedby="contract-read-hint"
            style={{
              padding: '12px 28px',
              borderRadius: 10,
              border: 'none',
              background: canSign ? colors.navy : colors.border,
              color: canSign ? '#FFFFFF' : colors.muted,
              fontSize: 14,
              fontWeight: 700,
              cursor: canSign ? 'pointer' : NOT_ALLOWED,
              transition: 'background 0.15s, color 0.15s',
              flexShrink: 0,
            }}
          >
            {signButtonLabel}
          </button>
          {isSigning && (
            <output style={{ fontSize: 13, color: colors.muted }}>
              Generating your signed copy…
            </output>
          )}
        </div>

        <p style={{ fontSize: 12, color: colors.muted, margin: 0, lineHeight: 1.5 }}>
          Your signature will include your name, the date and time (UTC), and your
          IP address as a verifiable audit record. A copy stamped with your signature
          will be submitted for review.
        </p>
      </div>
    </div>
  );
}
